import React from 'react';

import DocsMenu from './docs-menu';
import Section from './section';

import '../../stylesheets/docs.css';
import '../../stylesheets/code-prettify-sunburst.css';

class Docs extends React.Component {
  state = {
    displayDev: false,
  }

  componentDidMount() {
    const { PR } = window;
    PR.prettyPrint();
    this.setupSecretKeyListener();
  }

  componentWillUnmount() {
    this.teardownSecretKeyListener();
  }

  /**
   * Secret key (question-mark) listener to toggle display of hidden sections
   *
   * Setup, teardown, and helper functions
   */
  setupSecretKeyListener = () => {
    document.addEventListener('keypress', this.reDoc, false);
  }

  teardownSecretKeyListener = () => {
    document.removeEventListener('keypress', this.reDoc, false);
  }

  reDoc = (e) => {
    const keyNum = e.keyCode || e.which;
    // Check for '?', which is 63 in Unicode
    if (keyNum === 63) {
      const { displayDev } = this.state;
      this.setState({ displayDev: !displayDev });
    }
  }

  render() {
    const { displayDev } = this.state;
    const menuItems = [
      { url: '#overview', text: 'Overview', section: true },
      { url: '#quickstart', text: 'Quickstart' },
      { url: '#performance', text: 'Performance' },
      { url: '#pricing', text: 'Pricing & support' },
      { url: '#topics', text: 'User Guide', section: true },
      { url: '#authentication', text: 'Authentication' },
      { url: '#input', text: 'Media input' },
      { url: '#speakers', text: 'Speaker ID' },
      { url: '#poll', text: 'Status & results' },
      { url: '#models', text: 'Models' },
      { url: '#output', text: 'Output formats' },
      { url: '#callbacks', text: 'Callback URLs' },
      { url: '#jobs', text: 'Job management' },
      { url: '#advanced', text: 'Advanced Usage', section: true },
      { url: '#account', text: 'Account', hidden: !displayDev },
      { url: '#keys', text: 'Key management', hidden: !displayDev },
      { url: '#responses', text: 'HTTP codes' },
      { url: '#idempotency', text: 'Idempotent POST' },
      { url: '#sharing', text: 'Job Sharing' },
      { url: '#metadata', text: 'Metadata' },
      { url: '#media', text: 'Media' },
      { url: '#lookup', text: 'Lookup', hidden: !displayDev },
      { url: '#richify', text: 'Richify', hidden: !displayDev },
      { url: '#resubmission', text: 'Job Resubmission', hidden: !displayDev },
      { url: '#snapshots', text: 'Snapshots', hidden: !displayDev },
      { url: '#changelog', text: 'Recent changes', section: true, hidden: !displayDev }, // eslint-disable-line
    ];
    /* eslint-disable*/
    return (
<div className="docs">
  <div className="bodyContainer">
    <header>
      <div />
      <nav>
        <DocsMenu items={menuItems} dropdown />
      </nav>
      <h1>ASR API</h1>
      <p>
        {/* TODO: enable authentication from docs and show API keys modal */}
        {/* <a class="static-btnItem is-logIn" target="_blank" href="https://remeeting.com/signin">Sign In</a> */}
      </p>
    </header>
    <article>
      <nav>
        <DocsMenu items={menuItems} />
      </nav>
      <div className="content">
        <Section>
          <Section.Left>
            <a className="anchor" id="overview" />
            <h1>Overview</h1>
            <p>
              The <b>Automatic Speech Recognition</b> (ASR) API enables developers to use state-of-the-art speech-to-text transcription.
              It is designed for compatibility with the <a target="_blank" href="https://www.ibm.com/watson/developercloud/speech-to-text/api/v1/#async_methods">IBM Watson</a> service where this base URL:
            </p>
            <p><code className="prettyprint lang-sh">https://api.remeeting.com/asr/v1</code></p>
            <p>... is a drop-in replacement for endpoints under:</p>
            <p><code className="prettyprint lang-sh">https://stream.watsonplatform.net/speech-to-text/api/v1</code></p>
            <p>
              We implement the "asynchronous" endpoints, <code className="prettyprint">/recognitions</code> and <code className="prettyprint">/register_callback</code>.
            </p>
            <p>
              While supporting IBM's basic functionality, this ASR API also adds distinct features:
              <ul>
                <li>accept more flexible inputs</li>
                <li>produce more configurable outputs</li>
                <li>debug with <code className="prettyprint lang-sh">/log_callback</code></li>
                <li>monitor jobs and system <code className="prettyprint lang-sh">/stats</code></li>
                <li className={!displayDev && 'hidden'}>programmatic account and key management</li>
              </ul>
            </p>
            <p>
              Coming soon: <code className="prettyprint">/customizations</code>
            </p>
            <p>
              You can also find the Remeeting ASR API in the <a href="https://www.twilio.com/marketplace/add-ons/remeeting-speech-recognition" target="_blank">Twilio Add-on Marketplace</a>.
            </p>
          </Section.Left>
          <Section.Right>
            <h1>Examples</h1>
            <p>
              The requests below use <a className="code-link" href="https://curl.haxx.se/" target="_blank">curl</a> as a command-line HTTP client.
            </p>
            <h5>Top-level endpoint:</h5>
<pre className="prettyprint lang-bash">
curl https://api.remeeting.com/asr/v1
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a className="anchor" id="quickstart" />
            <h3>Quickstart</h3>
            <ol>
              <li>
                <b>Make an account</b> at <a href="https://remeeting.com/signup">remeeting.com/signup</a>.
                Verify your email and set a password, then navigate to "API Keys" under your profile to generate authentication credentials.
              </li>
              <p></p>
              <li>
                <b>POST a job</b> to <code className="prettyprint">/recognitions</code> with a hosted media URL, such as <a href="https://rmtg.co/hi.wav" target="_blank">rmtg.co/hi.wav</a>.
                Use your API key for authentication.
              </li>
              <p></p>
              <li>
                <b>GET results</b> under <code className="prettyprint lang-sh">/recognitions/&#123;id&#125;</code> with the <code className="prettyprint">"id"</code> from the previous JSON response.
                This resource may be polled until its <code className="prettyprint">"status"</code> becomes <code className="prettyprint">"completed"</code>.
              </li>
            </ol>
          </Section.Left>
          <Section.Right>
            <h5>POST a job:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: 'nowrap'}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-d media_url=rmtg.co/hi.wav<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>GET results:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: 'nowrap'}}>
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a className="anchor" id="performance" />
            <h3>Performance</h3>
            <p>
              See <a target="_blank" href="https://remeeting.com/benchmark">remeeting.com/benchmark</a> to assess the accuracy of our system, in an evaluation from the National Institute of Standards and Technology, comparing commercial ASR vendors.
              To replicate our benchmark results, run this shell script:
            </p>
            <code className="prettyprint lang-bash">bash &lt;(curl -sL rmtg.co/benchmark.sh)</code>
            <p>
              The ASR API has been optimized for dual-channel telephone conversations between native speakers of US English.
              It also excels in web-conferencing, and can handle multiple speakers on the same channel.
              {/* TODO: note better support for wideband audio */}
            </p>
            <p>
              Because it is a distributed workflow, service levels will depend on system load.
              We will maintain wait times under 1 hour, followed by faster than real-time processing speed.
              Under ideal conditions, the turnaround time is 10x faster than media playback.
            </p>
            <p>
              Due to overhead, relatively short recordings will process less efficiently.
              This service is best suited for high-throughput batch processing rather than low-latency interactive applications.
            </p>
            <p>
              System resources can be monitored by the ASR API <code className="prettyprint">/stats</code> endpoint.
              General notices and issues are posted at <a className="code-link" href="https://remeeting.com/status">remeeting.com/status</a>.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>System monitoring:</h5>
<pre className="prettyprint lang-bash">
curl https://api.remeeting.com/asr/v1/stats
</pre>
<pre className="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "current": {
      "processing_load": 12,
      "waiting_ratio": 0.0
   },
   "average": {
      "processing_speed": 8.5,
      "processing_time": 250.6,
      "waiting_time": 3.0
   }
}`}
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a className="anchor" id="pricing" />
            <h3>Pricing and support</h3>
            <p>
              Usage is priced at $0.01 per minute.
              No charge is incurred for intervals of long silence, e.g. while "on hold".
            </p>
            <p>
              Volume discounts are negotiable; we also support OEM licensing and private deployments.
              Please direct inquiries to <a target="_blank" href="mailto:sales@remeeting.com?Subject=ASR%20Inquiry">sales@remeeting.com</a>.
            </p>
            <p>
              Got questions?
              Reach out and chat with our team using the blue button in the bottom-right of this window.
              You can also send an email to <a target="_blank" href="mailto:support@remeeting.com?Subject=API%20Support">support@remeeting.com</a> or call our dedicated customer support phone number: <a href="tel:+14842752756">(HUH) ASK-ARLO</a> (A Real Live Operator).
            </p>
            <p>
              Product feedback and bug reports help improve this new service.
              We will respond within hours and resolve issues within days.
            </p>
            {/* TODO: integrate Intercom on this page */}
          </Section.Left>
          <Section.Right empty/>
        </Section>

        <Section>
          <Section.Left>
            <h3 />
            <p>
              You can also use our <code class="prettyprint">/feedback</code> endpoint to submit any bugs
              or questions you may have. It is not necessary to specify a job ID, but
              it may be helpful to provide for context with the feedback.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Submit feedback</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/v1/feedback<wbr />&nbsp;-u "&#123;KEY&#125;":<wbr />&nbsp;-d job_id="&#123;ID&#125;"<wbr />&nbsp;-d message="Remeeting rocks!"
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a className="anchor" id="topics" />
            <h1>User Guide</h1>
            <p>
              These topics describe many ways to use the ASR API, demonstrated with examples.
            </p>
          </Section.Left>
          <Section.Right empty />
        </Section>

        <Section>
          <Section.Left>
            <a className="anchor" id="authentication" />
            <h3>Authentication</h3>
            <p>
              <a target="_blank" href="https://remeeting.com/signup" target="_blank">Sign up</a> for an account at Remeeting.com.
              After verifying your email address and setting a password, sign in and click the profile icon in the top-right to select "API Keys".
              Use that web interface to create or delete these credentials.
            </p>
            <p>
              The API Key should be passed on HTTP requests in an <code className="prettyprint">Authorization:</code> header, as an OAuth-style Bearer token.
            </p>
            <p>
              The API key may also be passed as either the username or password via <a target="_blank" href="http://en.wikipedia.org/wiki/Basic_access_authentication">HTTP Basic authentication</a>.
              For brevity and convenience, our examples use <code className="prettyprint lang-sh">curl -u "&#123;KEY&#125;":</code>.
              (Pro-tip: you can also pass it in the URL, e.g. <code className="prettyprint">https://&#123;KEY&#125;@api.remeeting.com/</code>)
            </p>
            <p>
              Other authentication methods are supported for convenience, but necessarily not recommended:
              <ul>
                <li>HTTP Basic using the email address and password of a Remeeting account.</li>
                <li>Pass an <code className="prettyprint">api_key</code> parameter in a query string, form field, or JSON body.</li>
              </ul>
            </p>
          </Section.Left>
          <Section.Right>
            <h5>API Key as Bearer token:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-H "Authorization: Bearer &#123;KEY&#125;"
</pre>
            <h5>API Key as HTTP Basic username or password:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>Additional authentication alternatives:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-u "&#123;EMAIL&#125;":"&#123;PASSWORD&#125;"
</pre>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />?api_key="&#123;KEY&#125;"
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a className="anchor" id="input" />
            <h3>Media input</h3>
            <p>
              Send a POST request to the <code className="prettyprint">/recognitions</code> endpoint to create a job and initiate processing.
              There are several ways to transfer the recording:
              <p>
                <b>As a media URL</b> parameter:
                <ul>
                  <li><code className="prettyprint">media_url</code> must be reachable on the public Internet and retrieved via GET</li>
                </ul>
              </p>

              If the media is stored as a local file, it is possible to upload it to the ASR API:
              <p>
                <b>As multipart form data</b>:
                <ul>
                  <li><code className="prettyprint">metadata</code> can specify additional JSON parameters (cf. IBM Watson <a href="https://www.ibm.com/watson/developercloud/speech-to-text/api/v1/#recognize_sessionless_mp" target="_blank">"multipart"</a>)</li>
                  <li><code className="prettyprint">upload</code> specifies a single file.  (Multiple file upload is not yet supported.)</li>
                  {/* TODO: multiple files */}
                </ul>
              </p>
              <p>
                <b>As binary data</b> in a body with an optional header:
                <ul>
                  <li><code className="prettyprint">Content-Type:</code> (e.g. <code className="prettyprint">audio/wav</code>)</li>
                </ul>
              </p>
            </p>
            <p>
              Request headers can also indicate compressed content and chunk-encoded HTTP streaming:
              <ul>
                <li><code className="prettyprint">Content-Encoding: gzip</code></li>
                <li><code className="prettyprint">Transfer-Encoding: chunked</code></li>
              </ul>
            </p>
            <p>
              In general, request parameters can be sent to the ASR API as query strings, form fields, or JSON.
              For compatibility with IBM Watson, use query strings and send media files as binary data.
            </p>

            <h4>Media types</h4>
            <p>
              The ASR API will accept a wide variety of file formats and codecs, as supported by <a target="_blank" href="https://libav.org/documentation/general.html#Supported-File-Formats-and-Codecs">Libav</a>.
              There is no limit on the size of data that is uploaded, but please note that sending video files may be wasteful as only the audio stream will be extracted and processed.
              Audio signals should be sampled at 8 kHz or higher; if compressed, bitrates above 20 kbps work best.
            </p>
          </Section.Left>

          <Section.Right>
            <h5>Media URL as query string, form field, or JSON data:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />?media_url=rmtg.co/hi.wav<wbr />&nbsp;-X POST<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-d media_url=rmtg.co/hi.wav<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-H 'Content-Type: application/json'<wbr />&nbsp;-d '&#123;"media_url": "rmtg.co/hi.wav"&#125;'<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <p>
              <br /><br />
              For the next examples, download a file locally:
<pre className="prettyprint lang-sh">
curl -L rmtg.co/hi.wav > "test.wav"
</pre>
            </p>
            <h5>Media file in multipart form:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-F metadata='&#123;"part_content_type": "audio/wav"&#125;'<wbr />&nbsp;-F upload=@"test.wav"<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>Media file as binary data:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-H 'Content-Type: audio/wav'<wbr />&nbsp;--data-binary @"test.wav"<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>Streaming, compression, and transcoding:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
avconv -v quiet -i test.wav -f mp3 - | gzip -c | <wbr />curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-H 'Transfer-Encoding: chunked'<wbr />&nbsp;-H 'Content-Encoding: gzip'<wbr />&nbsp;-H 'Content-Type: audio/mp3'<wbr />&nbsp;--data-binary @-<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a className="anchor" id="speakers" />
            <h3>Speaker identification</h3>
            <p>
              {/* TODO: update this when we auto-select models?  Should 16khz imply 0 speakers? */}
              Recordings are assumed to contain two speakers engaged in conversation.
              Otherwise, it is necessary to indicate the <code className="prettyprint">speakers</code> parameter.
            </p>
            <p>
              Dual-channel recordings (not stereo) are highly recommended for 2-speaker conversations; this results in perfect speaker separation.
            </p>
            <p>
              If there are more speakers than the number of channels, automatic speaker separation (diarization) will be performed.
              It is best to have well-balanced participation of all speakers; for long recordings, this additional processing will be very slow.
              {/* TODO: per-channel diarization, labeling */}
            </p>
            <p>
              If you are unsure of the number of speakers, or if there are very many of them, specify <code className="prettyprint">speakers=0</code>.
              While this won't identify the speakers, it will disable the usual speaker adaptation mechanisms, which might improve recognition accuracy -- especially compared to specifying an incorrect number of speakers.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Specify single-speaker input:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />?speakers=1<wbr />&nbsp;-H 'Content-Type: audio/wav'<wbr />&nbsp;--data-binary @"test.wav"<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a className="anchor" id="poll" />
            <h3>Status and results</h3>
            <h4>Poll for job status</h4>
            <p>
              To check the current status of a job, note the newly created resource indicated by <code className="prettyprint">"id"</code> in the response to the POST request.
              Then send a GET to <code className="prettyprint lang-sh">/recognitions/&#123;id&#125;</code>.
              That URL can also be found in the <code className="prettyprint">"url"</code> value from the POST response as well as its <code className="prettyprint">Location:</code> header.
            </p>
            <p>
              The returned JSON message will contain a <code className="prettyprint">"status"</code> value which will be one of:
              <ul>
              <li>
                <p>
                  <code className="prettyprint">"waiting"</code> if the job is enqueued for processing, remaining in this state until there is capacity to begin.
                </p>
              </li>
              <li>
                <p>
                  <code className="prettyprint">"processing"</code> if the job is progressing.
                </p>
              </li>
              <li>
                <p>
                  <code className="prettyprint">"completed"</code> if processing has finished.
                </p>
              </li>
              <li>
                <p>
                  <code className="prettyprint">"failed"</code> if the processing failed.
                </p>
              </li>
              </ul>
            </p>
            <p>
              If the request includes a <code className="prettyprint">progress=true</code> parameter, the JSON message might also include a <code className="prettyprint">"progress"</code> value indicating the job's approximate percentage of completion.
            </p>
            <p>
              There may also be a <code className="prettyprint">"details"</code> field with explanatory information for the <code className="prettyprint">"failed"</code> state.
            </p>
            <p>
              The time needed to process a transcription will depend on many factors such as: recording duration, proportion of speech vs. silence, number of speakers, number of channels, file size, queue backlog, CPU contention, etc...
            </p>
            <p>
              To estimate the time when a job might be expected to complete, it may be helpful to refer to information from the <code className="prettyprint">/stats</code> endpoint, or visit <a href="https://remeeting.com/status">remeeting.com/status</a>.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Show progress while in "processing" status:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"<wbr />?progress<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre className="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "created": "1997-08-29T03:14:15.926535-04:00",
   "id": "42",
   "updated": "2004-07-25T16:18:03.398874-07:00",
   "status": "processing",
   "progress": 54
}`}
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <h4>Retrieve a result</h4>
            <p>
              Send a GET request to a <code className="prettyprint lang-sh">/recognitions/&#123;id&#125;</code> resource, as when polling for status.
              If it is <code className="prettyprint">"completed"</code>, the response now includes the final transcription results.
            </p>
            <p>
              Conforming to IBM Watson's specification of this representation, each <code className="prettyprint">"transcript"</code> string is found within an <code className="prettyprint">"alternatives"</code> list under a doubly-nested structure of <code className="prettyprint">"results"</code>.
              Such a segment corresponds to several seconds of speech that is determined to be spoken by a distinct speaker.
              The estimated <code className="prettyprint">"confidence"</code> value should correlate with ASR accuracy.
            </p>
            <p>
              A notable difference is that our <code className="prettyprint">"transcript"</code> exhibits automatic capitalization and punctuation, as well as removal of hesitations and repeated words.
              Alternatively, a <code className="prettyprint">"transcript_raw"</code> result is provided for direct word-level correspondence with some of the more complex output formats described below.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Response in "completed" status:</h5>
<pre className="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "created": "1997-08-29T03:14:15.926535-04:00",
   "id": "42",
   "updated": "2011-04-19T20:11:04.192011-04:00",
   "results": [
      {
         "result_index": 0,
         "results": [
            {
               "final": true,
               "alternatives": [
                  {
                     "transcript": "Hello, World. ",
                     "transcript_raw": "hello world ",
                     "confidence": 0.902
                  }
               ]
            },
            {
               "final": true,
               "alternatives": [
                  {
                     "transcript": "Hi. ",
                     "transcript_raw": "um hi ",
                     "confidence": 0.851
                  }
               ]
            }
         ]
      }
   ],
   "status": "completed"
}`}
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a class="anchor" id="models" />
            <h3>Models</h3>
            <h4>List models</h4>
            <p>
              To obtain a list of the currently supported models, with information detailing languages (including regional dialects) and audio sample rates amongst other information, send a GET request to <code class="prettyprint">/models</code> endpoint.
            </p>
            <p>
              Our system currently supports the following models:
              <ul>
                <li>
                  <p>
                    <code class="prettyprint">en-US_TelephoneModel</code>
                  </p>
                </li>
                <li>
                  <p>
                    <code class="prettyprint">en-US_VideoModel</code>
                  </p>
                </li>
                <li>
                  <p>
                    <code class="prettyprint">es-US_TelephoneModel</code>
                    (Spanish as spoken in the United States)
                  </p>
                </li>
                <li>
                  <p>
                    <code class="prettyprint">es-MX_TelephoneModel</code>
                    (Spanish as spoken in the US and northern Mexico)
                  </p>
                </li>
              </ul>
              <p>
                The "Telephone" models are best suited for 8 kHz telephony applications.
                Performance may degrade for audio that is recorded in different settings, such as far-field microphones in reverberant rooms with background noise.
              </p>
              <p>
                The "Video" model is better suited for wideband audio (16 kHz and higher).
                It was developed to transcribe online videos, and exhibits better robustness across a variety of acoustic conditions.
                Although it is mainly targeted to US English, this model performs relatively well with non-American accents also.
              </p>
              <p>
                All of our models support the <code class="prettyprint">"custom_language_model"</code> and <code class="prettyprint">"speaker_labels"</code> features.
                Language model customization is being developed in "preview" mode; please contact us if you would like to learn more about this functionality.
              </p>
            </p>
          </Section.Left>
          <Section.Right>
            <h5>List available models:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/models
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "models": [
      {
         "name": "en-US_TelephoneModel",
         "language": "en-US",
         "url": ".../v1/models/en-US_TelephoneModel",
         "rate": 8000,
         "supported_features": {
            "custom_language_model": true,
            "speaker_labels": true
         },
         "description": "US English Telephone Model."
      },
      {
         "name": "es-US_TelephoneModel",
         "language": "es-US",
         "url": ".../v1/models/es-US_TelephoneModel",
         "rate": 8000,
         "supported_features": {
            "custom_language_model": true,
            "speaker_labels": true
         },
         "description": "US Spanish Telephone Model."
      }
   ]
}`}
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <h4>Get a model</h4>
            <p>
              To obtain information about a specific model, send a GET request to the <code class="prettyprint lang-sh">/models/&#123;name&#125;</code> endpoint.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Get a specific model:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
  curl https://api.remeeting.com/asr/v1/models/<wbr />en-US_VideoModel
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <h4>Using models</h4>
            <p>
              By default, the system performs ASR using <code class="prettyprint">en-US_TelephoneModel</code>.
              {/* TODO: document automatic model selection based on detected sample rate */}
            </p>
            <p>
              If you would like to specify another model, you would make a POST request to the <code class="prettyprint">/recognitions</code> endpoint with the <code class="prettyprint">model</code> request parameter set to one of the available models, which may be listed as detailed in the section above.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Submit a job with a specific model:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
  curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-u "&#123;KEY&#125;":<wbr />&nbsp;-d media_url=rmtg.co/hi.wav<wbr />&nbsp;-d model=en-US_VideoModel
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a class="anchor" id="output" />
            <h3>Output formats</h3>
            <p>
              Completed recognitions can be viewed in various output representations.
              These are structurally similar to the IBM Watson Speech-To-Text API, and generally compliant with industry standards for <a href="https://dvcs.w3.org/hg/speech-api/raw-file/tip/speechapi.html" target="_blank">Web Speech API Specification</a>.
            </p>
            <p>
              However, our API allows parameters that modify the output representation to be specified on either an initial POST as well as on later GET requests.
            </p>
            <p>
              The following implement the same functionality as documented in IBM Watson's <a href="https://www.ibm.com/watson/developercloud/doc/speech-to-text/output.html" target="_blank">output features and parameters</a>:
            </p>
            <ul>
              <li><code class="prettyprint">"speaker_labels"</code>: show speaker label detected for each word interval</li>
              <li><code class="prettyprint">"timestamps"</code>: show start and end times for each word</li>
              <li><code class="prettyprint">"word_confidence"</code>: show confidence for each word in <code class="prettyprint">"transcript_raw"</code></li>
              <li><code class="prettyprint">"word_alternatives_threshold"</code>: show all words above this confidence</li>
            </ul>
            <p>
              Our API supports additional Boolean parameters that conveniently transform results:
            </p>
            <ul>
              <li><code class="prettyprint">"speaker_label_transcript"</code>: show speaker label alongside <code class="prettyprint">"transcript"</code></li>
              <li><code class="prettyprint">"timestamp_transcript"</code>: show start and end times alongside <code class="prettyprint">"transcript"</code></li>
              <li><code class="prettyprint">"word_alternatives"</code>: show all word alternatives (i.e. threshold is 0.0)</li>
              <li><code class="prettyprint">"compact"</code>: render JSON with no unneccessary whitespace.</li>
              <li><code class="prettyprint">"tty"</code>: colored non-JSON representation useful for inspecting in a terminal</li>
            </ul>
            <p>
              All of the above output parameters are optional.
              A Boolean parameter is considered true if passed without setting an explicit value (e.g. <code class="prettyprint">?compact&tty</code>).
            </p>
            <p>
              Specifying a <code class="prettyprint">"format"</code> parameter will produce alternative representations:
              <ul>
                <li><code class="prettyprint">ibm</code> or <code class="prettyprint">watson</code>is the default, for which all of the above parameters apply</li>
                <li><code class="prettyprint">remeeting</code> is our custom JSON; none of the above parameters apply</li>
                <li><code class="prettyprint">sherlock</code> is our custom JSON, embedded in a "Watson-like" structure</li>
                <li><code class="prettyprint">ttml</code> (Timed Text Markup Language) is coming soon...</li>
                <li><code class="prettyprint">vlf</code> (Veritone Lattice Format) is coming soon...</li>
              </ul>
            </p>
            <p>
              Parameters that are not specified on a GET request will inherit values that may have been specified on the initial POST.
              A callback for the <code class="prettyprint">completed_with_results</code> event includes the same output representation that would be retrieved by an unparameterized GET request.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>POST a job that will show speaker labels and all word alternatives:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-d media_url=rmtg.co/hi.wav<wbr />&nbsp;-d speaker_labels=true<wbr />&nbsp;-d word_alternatives_threshold=0.0<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>GET that job, but without speaker labels, and now show word-level timestamps:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"<wbr />&nbsp;-d speaker_labels=false<wbr />&nbsp;-d timestamps=true<wbr />&nbsp;-u "&#123;KEY&#125;":<wbr />&nbsp;-X<wbr />&nbsp;GET
</pre>
            <h5>Show transcript-level timestamps and compact JSON:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"<wbr />?timestamp_transcript&amp;compact<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap", backgroundColor: "black"}}>
&#123;"created":"2017-03-20T13:10:56.738031+00:00","id":"e2c"<wbr />,"updated":"2017-03-20T13:11:26.231164+00:00","results":<wbr />[&#123;"result_index":0,"results":[&#123;"final":true,"alternative<wbr />s":[&#123;"transcript":"hello world ","confidence":1,"timesta<wbr />mp":[0,1.36]&#125;]&#125;,&#123;"final":true,"alternatives":[&#123;"transcri<wbr />pt":"um hi ","confidence":1,"timestamp":[0.6,2]&#125;]&#125;]&#125;],"s<wbr />tatus":"completed"&#125;
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a class="anchor" id="callbacks" />
            <h3>Callback URLs</h3>
            <p>
              On POST, specify a <code class="prettyprint">callback_url</code> parameter to cause notifications to be sent from the ASR API to the specified Internet location.
              JSON messages will be sent over HTTP(S) as POST requests.
            </p>
            <p>
              Specify <code class="prettyprint">events</code> (comma-delimited list) to be notified when a job status changes:
              <ul>
                <li><code class="prettyprint">"started"</code> (from waiting to processing)</li>
                <li><code class="prettyprint">"failed"</code> (while processing)</li>
                <li><code class="prettyprint">"completed"</code> (after processing)</li>
                <li><code class="prettyprint">"completed_with_results"</code> returns JSON as retrieved with a GET request</li>
              </ul>
              The first three are subscribed by default; the last two are mutually exclusive.
              For IBM Watson compatibility, specify these events with a <code class="prettyprint">recognitions.*</code> prefix.
            </p>
            <p>
              Callback notifications may be verified with an HMAC-SHA1 signature (base64-encoded) when a <code class="prettyprint">user_secret</code> is supplied as an encryption key.
              POST requests sent from the ASR API will set the <code class="prettyprint">X-Callback-Signature:</code> header.
              If parameters for <code class="prettyprint">basic_auth_username</code> and/or <code class="prettyprint">basic_auth_password</code> are specified, an <code class="prettyprint">Authorization:</code> header will be set as well.
            </p>
            <p>
              Specify arbitrary <code class="prettyprint">user_token</code> strings to be included on the callback notifications.
            </p>
            <p>
              A convenient way to debug callbacks is to set the <code class="prettyprint">email_callback</code> parameter.
              Check your email address registered with a Remeeting account to view the content of a callback notification.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Use callback URL:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-d media_url=rmtg.co/hi.wav<wbr />&nbsp;-d callback_url=rmtg.co/v1/log_callback/world<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>Simple callback notification:</h5>
<pre class="prettyprint" style={{backgroundColor: "black"}}>
&#123;"id":"42","event":"recognitions.started"&#125;
</pre>
            <h5>Complex callback usage, debugging via email:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-d media_url=rmtg.co/hi.wav<wbr />&nbsp;-d email_callback=true<wbr />&nbsp;-d basic_auth_username=bob<wbr />&nbsp;-d basic_auth_password=abc<wbr />&nbsp;-d user_secret=swordfish<wbr />&nbsp;-d user_token=test<wbr />&nbsp;-d events=completed_with_results,failed<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <h4>Registering callbacks</h4>
            <p>
              The ASR API does not require callback URLs to be registered.
              However, doing so is recommended because it allows sensitive credentials to be specified just once at configuration rather than being explicitly passed on every POST request.
            </p>
            <p>
              The registration process is based on IBM Watson's service: POST to <code class="prettyprint">register_callback</code> with parameters including a <code class="prettyprint">callback_url</code>.
              The specified callback URL should respond to an immediate GET by echoing a <code class="prettyprint">challenge_string</code> query string sent by the ASR API.
              If <code class="prettyprint">user_secret</code> is also specified on the registration request, its values will be saved and then applied to sign future notifications sent from the ASR to the registered callback URL.
            </p>
            <p>
              This ASR API implements callback registration with the following additional functionality:
              <ul>
                <li>if the <code class="prettyprint">challenge=false</code> parameter is requested, the callback URL need not reply to a GET in order to be registered</li>
                <li>a <code class="prettyprint">callback_url_prefix</code> may be used to register multiple URLs.  When sending a notification to a callback URL, parameters may be set from its longest registered prefix.</li>
                <li>parameters for <code class="prettyprint">basic_auth_username</code> and <code class="prettyprint">basic_auth_password</code> may be registered and applied similarly to <code class="prettyprint">user_secret</code></li>
              </ul>
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Register callback URL with secret, use it without:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/register_callback<wbr />&nbsp;-d callback_url=rmtg.co/v1/log_callback/user_secret=ah/<wbr />&nbsp;-d user_secret=ah<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-d callback_url=rmtg.co/v1/log_callback/user_secret=ah/<wbr />&nbsp;-d media_url=rmtg.co/hi.wav<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>Register and use callback URL prefix:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/register_callback<wbr />&nbsp;-d callback_url_prefix=rmtg.co/v1/log_callback/my/<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-d callback_url=rmtg.co/v1/log_callback/my/url<wbr />&nbsp;-d media_url=rmtg.co/hi.wav<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <h4>Debugging callbacks</h4>
            <p>
              The previous examples have hinted at the possibility of testing callback URLs using two special features: <code class="prettyprint lang-sh">rmtg.co/v1</code> is a shorthand proxy for the ASR API, which also implements a <code class="prettyprint lang-sh">/log_callback</code> endpoint.
              The purpose is to facilitate development by demonstrating services that respond appropriately to register and receive callback URLs.
            </p>
            <p>
              GET requests to <code class="prettyprint lang-sh">/log_callback/&#123;path&#125;</code> respond with a recent history of POST requests received at those URLs.
              Because they are world-readable, be careful to send only public data or chose random paths.
              The storage is cleared every few hours.
            </p>
            <p>
              Moreover, it is possible to encode credentials into these URLs (obviously insecure) to test the verification of an HMAC-SHA1 signature computed over message content, and authenticate requests with HTTP Basic:
              <p>
                <code class="prettyprint lang-sh">/log_callback/user_secret=&#123;str&#125;/&#123;path&#125;</code>
                <br />
                <code class="prettyprint lang-sh">/log_callback/basic_auth=&#123;un:pw&#125;/&#123;path&#125;</code>
              </p>
            </p>
            <p>
              These paths respond appropriately to a GET request with a <code class="prettyprint lang-sh">challenge_string</code> parameter, as sent by the ASR API to register callback URLs.
            </p>
            <p>
              Alternatively: check out <a href="https://www.twilio.com/blog/2015/09/6-awesome-reasons-to-use-ngrok-when-testing-webhooks.html" target="_blank">ngrok</a>, recommended by our friends at Twilio.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>POST and GET logs:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl -L rmtg.co/v1/log_callback/"test"<wbr />&nbsp;-d "foo=bar"
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl -L rmtg.co/v1/log_callback/"test"
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`[{
  "datetime": "2017-03-15T18:21:49.710218+00:00",
  "headers": {
   "Accept": "*/*",
   "Connection": "Keep-Alive",
   "Content-Length": "7",
   "Content-Type": "application/x-www-form-urlencoded",
   "Host": "api.remeeting.com",
   "User-Agent": "curl/7.51.0"
  },
  "body": "foo=bar"
}]`}
</pre>
            <h5>Echo challenge string when registering callback URL:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl -L rmtg.co/v1/log_callback/"test"<wbr />&nbsp;-d challenge_string="abc"
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl -L rmtg.co/v1/log_callback/user_secret="123"/"test"<wbr />?challenge_string="abc"<wbr />&nbsp;-H "X-Callback-Signature: VAsMU9SSWDe9krP3Gr56nXC2dsQ="
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl -L rmtg.co/v1/log_callback/basic_auth="me:1"/"test"<wbr />?challenge_string="abc"<wbr />&nbsp;-u "me:1"
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a class="anchor" id="jobs" />
            <h3>Job management</h3>
            <h4>Listing jobs</h4>
            <a class="anchor" id="delete" />
            <p>
              To view a list of the 10 most recent transcriptions in your account, sorted by creation time, send a GET request without any specific <code class="prettyprint">&#123;id&#125;</code> identifier.
            </p>
            <p>
              The <code class="prettyprint">limit</code> parameter can list more transcriptions, up to 100.
              The <code class="prettyprint">before</code> parameter will show items created prior to that date and time (<a href="https://en.wikipedia.org/wiki/ISO_8601" target="_blank">ISO 8601</a> format).
              The <code class="prettyprint">status</code> parameter filters items with a matching stage of processing.
            </p>

            <h4>Deleting a job</h4>
            <p>
              Send a DELETE request to <code class="prettyprint lang-sh">/recognitions/&#123;id&#125;</code> to delete the specified resource.
              You may delete a job that is currently processing or enqueued.
              Transcription results are available indefinitely.
            </p>
            <p>
              Coming soon: <code class="prettyprint">results_ttl</code> parameter.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>GET all jobs:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>GET more, older, failed jobs:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-d limit=50<wbr />&nbsp;-d before=2017-01-23<wbr />&nbsp;-d status=failed<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>DELETE a job:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"<wbr />&nbsp;-X DELETE<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a class="anchor" id="advanced" />
            <h1>Advanced Usage</h1>
            <p>
              These topics briefly describe additional functionality of the ASR API.
            </p>
          </Section.Left>
          <Section.Right empty />
        </Section>

        <Section hidden={!displayDev}>
          <Section.Left>
            <a class="anchor" id="account" />
            <h3>Account</h3>
            <p>
              Generally, users should visit the <a target="_blank" href="https://remeeting.com/signup" target="_blank">Remeeting.com</a> website to manage their accounts.
              However, the same functionality is available by directly accessing this ASR API.
            </p>
            <p>
              To create a new account, POST to <code class="prettyprint lang-sh">/account</code> using HTTP Basic authentication with your email address as the username; set an empty password.
            </p>
            <p>
              You should then receive an email message that includes a <code class="prettyprint">"verification_token"</code> (single-use "nonce") that can be passed in a POST to <code class="prettyprint lang-sh">/account/password</code>.
              Use HTTP Basic authentication again, but now indicate your desired password.
            </p>
            <p>
              To change your password, first POST to <code class="prettyprint lang-sh">/account/verify</code> with your current password specified via HTTP Basic authentication.
              This will issue a new <code class="prettyprint">"verification_token"</code>
            </p>
            <p>
              If you forget your password, POST to <code class="prettyprint lang-sh">/account/verify</code> with an empty password.
              An email will be sent with a new <code class="prettyprint">"verification_token"</code>.
            </p>
            {/* Undocumented feature: POST to /account/verify with the verification_token parameter to issue a new verification_token */}
            <p>
              A GET request to <code class="prettyprint lang-sh">/account/verify</code> will not issue a new <code class="prettyprint">"verification_token"</code>, but can also be used to verify whether credentials (including an API key) are currently valid.
            </p>
            <p>
              An authenticated GET request to <code class="prettyprint lang-sh">/account</code> will display current account information.
              Hierarchical sections can be edited with a POST to <code class="prettyprint lang-sh">/account/&lt;section&gt;</code> that includes request parameters of the same name and type as the displayed fields.
            </p>
            {/* Undocumented feature: GET to /account/<section> with the verification_token parameter to issue a new verification_token */}
          </Section.Left>
          <Section.Right>
            <h5>Create a new account and set a password:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
  curl https://api.remeeting.com/asr/v1/account<wbr />&nbsp;-u "&#123;EMAIL&#125;":<wbr />&nbsp;-X POST
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/password<wbr />&nbsp;-d verification_token="&#123;TOKEN&#125;"<wbr />&nbsp;-u "&#123;EMAIL&#125;":"&#123;PASSWORD&#125;"
</pre>
            <h5>Issue new verification tokens to change current (or reset forgotten) password:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/verify<wbr />&nbsp;-X POST<wbr />&nbsp;-u "&#123;EMAIL&#125;":"&#123;PASSWORD&#125;"
</pre>
            <h5>Verify credentials via GET:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/verify<wbr />&nbsp;-u "&#123;EMAIL&#125;":"&#123;PASSWORD&#125;"
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/verify<wbr />&nbsp;-d verification_token="&#123;TOKEN&#125;"
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/verify<wbr />&nbsp;-H "Authorization Bearer &#123;KEY&#125;"
</pre>
            <h5>Edit the city field of the profile section of account information:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/profile<wbr />&nbsp;-d city="&#123;CITY&#125;"<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section hidden={!displayDev}>
          <Section.Left>
            <a class="anchor" id="keys" />
            <h3>Key management</h3>
            <p>
              Generally, users should visit the <a target="_blank" href="https://remeeting.com/signup" target="_blank">Remeeting.com</a> website to manage their API keys.
              However, the same functionality is available by directly accessing this ASR API.
            </p>
            <p>
              To generate a new key, POST to <code class="prettyprint lang-sh">/account/keys</code> with your email and password as HTTP Basic authentication.
              A <code class="prettyprint lang-sh">name</code> parameter can serve as a user-friendly identifier.
            </p>
            {/* Undocumented feature:
            <p>
              Specifying the <code class="prettyprint lang-sh">ttl</code> parameter will set a time-to-live expiration on the API Key.
              This can be useful as a short-lived token for an interactive client session.
            </p>
            */}
            <p>
              GET <code class="prettyprint lang-sh">/account/keys</code> to view a list of your API Keys{/* (excluding short-lived tokens with a <code class="prettyprint lang-sh">ttl</code>)*/}.
              {/* TODO: enable listing of short-lived tokens */}
              This listing can be paginated with the <code class="prettyprint">limit</code> and <code class="prettyprint">before</code> parameters.
            </p>
            <p>
              An API Key is a secret token that should be safeguarded by the user; it will only be displayed upon the initial POST.
              To be handled as a resource on the ASR API, we reference an <code class="prettyprint lang-sh">id</code> computed as the SHA-256 hash of the API Key.
            </p>
            <p>
              GET <code class="prettyprint lang-sh">/account/key/&#123;id&#125;</code> to view a specific key.
              You may also POST or DELETE to this endpoint, requiring HTTP Basic authencation with your email and password -- unless the request is authenticated with the same API key that is to be modified.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Create a new API Key with a user-friendly name</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/keys<wbr />&nbsp;-u "&#123;EMAIL&#125;:&#123;PASSWORD&#125;"<wbr />&nbsp;-d name="&#123;NAME&#125;"
</pre>
            {/* Undocumented feature:
            <h5>Create a short-lived API Key (will not be listed)</h5>
<pre class="prettyprint lang-sh" style="white-space: nowrap">
curl https://api.remeeting.com/asr/v1/account/keys<wbr />&nbsp;-u "{EMAIL}":"{PASSWORD}"<wbr />&nbsp;-d ttl="{SECONDS}"
</pre>
            */}
            <h5>List keys</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/keys<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>Access a specific key by its hashed identifier; modify and delete itself</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/key/"&#123;id&#125;"<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/key/"&#123;id&#125;"<wbr />&nbsp;-d ttl="&#123;SECONDS&#125;"<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/account/key/"&#123;id&#125;"<wbr />&nbsp;-X DELETE<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a class="anchor" id="responses" />
            <h3>HTTP codes</h3>
            <p>
              Upon successful submission, the ASR API responds with an HTTP code of <code class="prettyprint">201 CREATED</code> and a JSON message.
              This may include a list of <code class="prettyprint">"warnings"</code> indicating that a bad request will be attempted anyway.
              Set the <code class="prettyprint">strict</code> request parameter to trigger an error instead.
            </p>
            <p>
              If there is an error, an appropriate HTTP code and JSON message should be returned.
              For errors to respond with a different code (e.g. <code class="prettyprint">200 OK</code>) specify the <code class="prettyprint">http_error_code</code> parameter.
              The standard HTTP error code will still be reported in the <code class="prettyprint">"code"</code> field of the JSON message.
            </p>
            <p>
              Specify the <code class="prettyprint">http_success_code</code> parameter to receive a response with a different code for successful requests, such as a <code class="prettyprint">202 ACCEPTED</code>.
            </p>
            <p>
              Setting <code class="prettyprint">http_success_empty</code> will respond with an empty body, suitable for a <code class="prettyprint">204 NO CONTENT</code>.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Warnings on a successful POST:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />&nbsp;-H 'Content-Type:'<wbr />&nbsp;--data-binary @test.wav<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "created": "1997-08-29T03:14:15.926535-04:00",
   "id": "42",
   "url": "https://api.remeeting.com/asr/v1/...",
   "status": "waiting",
   "warnings": ["Please set a 'Content-Type:' header"]
}`}
</pre>
            <h5>Overriding the HTTP error code:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />?http_error_code=200<wbr />&nbsp;-w 'HTTP Code: %&#123;http_code&#125;\n'
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "code": 401,
   "code_description": "Unauthorized",
   "error": "Missing 'Authorization:' header"
}
HTTP code: 200`}
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a class="anchor" id="idempotency" />
            <h3>Idempotent POST</h3>
            <p>
              The ASR API supports idempotent POST to allow safely retried requests.
              Provide a random string in the <code class="prettyprint">X-Remeeting-Idempotency-Key:</code> of a POST to the <code class="prettyprint lang-sh">/recognitions</code> endpoint.
            </p>
            <p>
              If multiple requests use the same identifier, at most one will receive a <code class="prettyprint">201 CREATED</code> response.
              Others will receive <code class="prettyprint">204 NO CONTENT</code> responses if the resource was previously created.
            </p>
            <p>
              Coming soon: the ASR API will verify request signatures, honoring invocation time and TTL.
            </p>
            <p>
              A POST to the <code class="prettyprint lang-sh">/register-callback</code> endpoint is always idempotent and does not require a special header.
              Per IBM's standard, this will return <code class="prettyprint">200 OK</code> if a callback URL was previously registered.
              Contact <a target="_blank" href="mailto:support@remeeting.com?Subject=Deregister%20Callback%20URL">support@remeeting.com</a> if you need to deregister a callback URL.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>Idempotent job submission:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions<wbr />?media_url=rmtg.co/hi.wav<wbr />&nbsp;-X POST<wbr />&nbsp;-H "X-Remeeting-Idempotency-Key: &#123;UNIQUE_STRING&#125;"<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>

        <Section>
          <Section.Left>
            <a class="anchor" id="sharing" />
            <h3>Job Sharing</h3>
            <p>
              Job sharing is available so that people who did not create a job are still able to view it.
              This is done by creating a "share key" so that a user's access token is not needed.
              After a share key is deleted, it can no longer be used by anyone to GET job data.
            </p>
          </Section.Left>
          <Section.Right>
            <h5>POST a share key to share a job:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"<wbr />&nbsp;/share_keys/<wbr />&nbsp;-X POST<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
            <h5>GET a job using a share key and no access token:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions/<wbr />&nbsp;"&#123;id&#125;"/share_key="&#123;SHARE_KEY&#125;"
</pre>
            <h5>DELETE a share key:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/recognitions/<wbr />&nbsp;"&#123;id&#125;"/share_keys/"&#123;SHARE_KEY&#125;"<wbr />&nbsp;-X DELETE -u "&#123;KEY&#125;":
</pre>
          </Section.Right>
        </Section>
        <Section>
          <Section.Left>
            <a className="anchor" id="metadata" />
            <h3>Metadata</h3>
            <p>
              The API stores data like meeting start time, duration, and
              speakers in recognitions as <code class="prettyprint lang-sh">metadata</code>. If a recognition has metadata,
              going to the <code class="prettyprint lang-sh">/recognition/&#123;id&#125;/metadata</code> endpoint returns
              the <code class="prettyprint lang-sh">"id"</code>, <code class="prettyprint lang-sh">"updated"</code>, and
              <code class="prettyprint lang-sh">"metadata"</code> fields in the recognition.
            </p>
            <ul>
              <li><code class="prettyprint lang-sh">"id"</code> - a string that represents the id of a particular recognition.</li>
              <li><code class="prettyprint lang-sh">"updated"</code> - an ISO-formatted date and time string of the last time the recognition's metadata was updated.</li>
              <li><code class="prettyprint lang-sh">"metadata"</code> - a JSON containing the metadata for the meeting with ID <code class="prettyprint">&#123;id&#125;</code>.</li>
            </ul>
            <p>
              To create an new meeting with metadata associated with it, use POST with a given
              metadata parameter in JSON format. If successful, the recognition is created with the
              specified metadata.
            </p>
            <p>
              To update a meeting's metadata, use PUT with a JSON of the new metadata you wish to
              associate with a given meeting. Updating metadata with a PUT does not merge keys and
              will overwrite the data. The argument <code class="prettyprint lang-sh">if_updated</code>
              is a datetime string parameter not stored in the metadata, but taken from the response
              of the <code class="prettyprint lang-sh">/metadata</code> endpoint.
              <code class="prettyprint lang-sh">if_updated</code> can be passed in a PUT request to
              make it conditional. The API checks this parameter when PUTing to make sure it matches
              <code class="prettyprint lang-sh">updated</code> in our database before
              overwriting. If it does not match, the request will return a
              <code class="prettyprint">403</code> error until the newest metadata is pulled.
            </p>
          </Section.Left>

          <Section.Right>
            <h5>Example GET /recognitions/$id/metadata request:</h5>
<pre className="prettyprint lang-sh">
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"/metadata<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "id": "45f4",
   "updated": "2020-02-21T20:18:10.702375+00:00",
   "metadata": {
      "hello": "world"
   }
}`}
</pre>
            <h5>POST a job with metadata:</h5>
<pre className="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
    curl -X POST https://api.remeeting.com/v1/recognitions<wbr />&nbsp;-H "Content-Type: application/json"<wbr />&nbsp;-d '&#123;"media_url": "rmtg.co/hi.wav",<wbr />&nbsp;  "metadata": "&#123;\"hello\": \"world\"&#125;"&#125;'<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "created": "2020-02-21T19:46:43.946867+00:00",
   "id": "45f4",
   "media_url": "rmtg.co/hi.wav",
   "metadata": {
      "hello": "world"
   },
   "url": "https://api.remeeting.com/asr/v1/recognitions/45f4",
   "status": "waiting"
}`}
</pre>
          </Section.Right>
        </Section>
        <Section>
          <Section.Left>
            <a className="anchor" id="media" />
            <h3>Media</h3>
            <p>
              The API also stores the media that was passed to the job in <code className="prettyprint lang-sh">media_url</code>, <code className="prettyprint lang-sh">audio_url</code>, <code className="prettyprint lang-sh">video_url</code> and <code className="prettyprint lang-sh">thumbnail_url</code>.
              These URLs lead to the media submitted for the specified job.
            </p>
          </Section.Left>

          <Section.Right>
            <h5>GET /recognitions/$id/audio_url request:</h5>
<pre className="prettyprint lang-sh">
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"/audio_url<wbr />&nbsp;-u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
  "audio_url": "https://mrp-user.s3.amazonaws.com/..."
}`}
</pre>
          </Section.Right>
        </Section>
        <Section hidden={!displayDev}>
          <Section.Left>
            <a className="anchor" id="lookup" />
            <h3>Lookup</h3>
            <p>
              The lookup endpoint has three paths that provide information about the lexicon,
              language model, and pronunciation of words:
            </p>
            <ul>
              <li><code class="prettyprint lang-sh">/lookup/lexicon/&#123;hash&#125;/&#123;word&#125;</code> - returns information we have on a word in the lexicon.</li>
              <li><code class="prettyprint lang-sh">/lookup/lm/&#123;hash&#125;</code> - returns the Ngrams for the language model.</li>
              <li><code class="prettyprint lang-sh">/lookup/pronunciation/&#123;word&#125;</code> - returns the pronunciation of a specified word.</li>
            </ul>
            <p>
              <code class="prettyprint lang-sh">/lookup/pronunciation/&#123;word&#125;</code> will return generated pronunciations for the word if it does not exist in the lexicon.
            </p>
          </Section.Left>

          <Section.Right>
            <h5>GET /lookup/lexicon request:</h5>
<pre className="prettyprint lang-sh">
curl https://api-dev.remeeting.com/asr/v1/lookup/lexicon/"&#123;hash&#125;"/hello
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "pronunciations": [
      {
         "probability": 1.0,
         "probability_silence_after": 0.57,
         "probability_silence_before_factor": 3.95,
         "probability_nonsilence_before_factor": 0.28,
         "pronunciation": "HH EH L OW"
      },
      {
         "probability": 0.9391,
         "probability_silence_after": 0.52,
         "probability_silence_before_factor": 4.64,
         "probability_nonsilence_before_factor": 0.22,
         "pronunciation": "HH AH L OW"
      }
   ],
   "word": "hello"
}`}
</pre>
<h5>GET /lookup/lm request:</h5>
<pre className="prettyprint lang-sh">
curl https://api.remeeting.com/asr/v1/lookup/lm/"&#123;hash&#125;"
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "ngrams": [
      100425,
      8447798,
      5013295
   ],
   "size_mib": 8
}`}
</pre>
<h5>GET /lookup/pronunciation request for a word that is not in the lexicon:</h5>
<pre className="prettyprint lang-sh">
curl https://api-dev.remeeting.com/asr/v1/lookup/pronunciation/wabbajack
</pre>
<pre class="prettyprint lang-js" style={{backgroundColor: "black"}}>
{`{
   "pronunciations": [
      "W AH B AA JH AE K",
      "W AA B AA JH AE K",
      "W AE B AH JH AE K"
   ]
}`}
</pre>
          </Section.Right>
        </Section>
        <Section hidden={!displayDev}>
          <Section.Left>
            <a className="anchor" id="richify" />
            <h3>Richify</h3>
            <p>
              Richification is the process of formatting and cleaning a string of words into a more
              readable form. Sending a JSON with the key <code class="prettyprint">"transcript_raw"</code> to <code class="prettyprint lang-sh">/richify</code> returns a richified version of the text.
            </p>
          </Section.Left>

          <Section.Right>
            <h5>Richification request:</h5>
<pre class="prettyprint lang-sh" style={{whiteSpace: "nowrap"}}>
curl https://api.remeeting.com/asr/v1/richify<wbr />&nbsp;-d '{`{"transcript_raw": "no luke i am your father"}`}'<wbr />&nbsp;-H "Content-Type: application/json"
</pre>
<pre class="prettyprint lang-sh" style={{backgroundColor: "black"}}>
{`{
  "transcript_formatted": "No Luke, I am your father."
}`}
</pre>
          </Section.Right>
        </Section>
        <Section hidden={!displayDev}>
          <Section.Left>
            <a className="anchor" id="resubmission" />
            <h3>Job Resubmission</h3>
            <p>
              If you want to rerun a job without reuploading its media, the API supports job
              resubmission. You can only resubmit jobs after they have finished processing or
              finished reprocessing. Some parameters from the original POST are retained if they
              are not specified in the resubmission PUT request.
            </p>
          </Section.Left>

          <Section.Right>
            <h5>Resubmission request</h5>
<pre className="prettyprint lang-sh">
curl -u "&#123;KEY&#125;": -X PUT https://api.remeeting.com/asr/v1/recognition/"&#123;id&#125;" -H "Content-Type: application/json" -d '&#123;"language_model_weight": 0&#125;'
</pre>
<pre class="prettyprint lang-sh" style={{backgroundColor: "black"}}>
{`{
  "created": "2020-05-20T22:38:09.137589+00:00",
  "id": "{id}",
  "url": "https://api.remeeting.com/asr/v1/recognition/{id}",
  "status": "waiting"
}`}
</pre>
          </Section.Right>
        </Section>
        <Section hidden={!displayDev}>
          <Section.Left>
            <a className="anchor" id="snapshots" />
            <h3>Snapshots</h3>
            <p>
              Snapshots are used to make revisitable copies of a job with all of its relevant data.
              This allows for saving information about a job before changing it or rerunning it.
            </p>
          </Section.Left>

          <Section.Right>
            <h5>Create snapshot:</h5>
<pre class="prettyprint lang-sh">
curl -X POST https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"/snapshots/ -u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-sh" style={{backgroundColor: "black"}}>
{`{
  "snapshot_id": "2020-05-21T16:39:43.509572+00:00"
}`}
</pre>
            <h5>View snapshot:</h5>
<pre class="prettyprint lang-sh">
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"/snapshots/"&#123;snapshot_id&#125;" -u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-sh" style={{backgroundColor: "black"}}>
{`{
  "snapshot_id": "2020-05-21T17:08:57.888392+00:00",
  "created": "2020-05-20T22:38:09.137589+00:00",
  "id": "{id}",
  "updated": "2020-05-20T22:41:10.201355+00:00",
  "results": "{results}",
  "status": "completed"
}`}
</pre>
            <h5>View all snapshots:</h5>
<pre class="prettyprint lang-sh">
curl https://api.remeeting.com/asr/v1/recognitions/"&#123;id&#125;"/snapshots/ -u "&#123;KEY&#125;":
</pre>
<pre class="prettyprint lang-sh" style={{backgroundColor: "black"}}>
{`{
  "snapshots": [
    "2020-05-21T17:08:57.888392+00:00",
    "2020-05-21T17:08:45.463178+00:00",
    "2020-05-21T16:39:43.509572+00:00"
  ]
}`}
</pre>
          </Section.Right>
        </Section>
        <Section hidden={!displayDev}>
          <Section.Left>
            <a class="anchor" id="changelog" />
            <h1>Recent Changes</h1>
            <p>
              Significant changes to the ASR API will be noted here.
              Our aim is to fix bugs and add new features that do not break backwards-compatibility with the current version.
              <ul>
                <li>
                  <h4>2020 Feb 28</h4>
                  <p>
                    Introduced new things:
                    <ul>
                      <li>
                        Added job resubmission
                      </li>
                      <li>
                        Added <code class="prettyprint lang-sh">/lookup</code> endpoints
                      </li>
                      <li>
                        Added <code class="prettyprint lang-sh">/metadata</code> endpoint
                      </li>
                      <li>
                        Added job sharing
                      </li>
                      <li>
                        Added <code class="prettyprint lang-sh">/richify</code> endpoint
                      </li>
                      <li>
                        Added <code class="prettyprint lang-sh">{`/{audio,media,thumbnail,video}_url`}</code> endpoints
                      </li>
                    </ul>
                  </p>
                </li>
                <li>
                  <h4>2018 Oct 31</h4>
                  <p>
                    Introduced account management functionality under <code class="prettyprint lang-sh">/account</code>:
                    <ul>
                      <li>
                        Basic signup: POST to <code class="prettyprint lang-sh">/account</code> and then <code class="prettyprint lang-sh">/account/password</code>
                      </li>
                      <li>
                        POST or GET <code class="prettyprint lang-sh">/account/verify</code> to change or verify credentials
                      </li>
                      <li>
                        API key management under <code class="prettyprint lang-sh">/account/keys</code>
                      </li>
                    </ul>
                  </p>
                </li>
                <li>
                  <h4>2018 Sep 23</h4>
                  <p>
                    Added <code class="prettyprint">/models</code> endpoint with support for multiple languages:
                    <ul>
                      <li>
                        Renamed <code class="prettyprint">en-US_NarrowbandModel</code> to <code class="prettyprint">en-US_TelephoneModel</code> (default)
                      </li>
                      <li>
                        Added <code class="prettyprint">en-US_VideoModel</code> to better support non-telephony audio (beta)
                      </li>
                      <li>
                        Added <code class="prettyprint">en-US_TelephoneModel</code> for British English.
                      </li>
                      <li>
                        Added <code class="prettyprint">es-US_TelephoneModel</code> and <code class="prettyprint">es-MX_TelephoneModel</code> for Spanish.
                      </li>
                    </ul>
                  </p>
                  <p>
                    Other incremental improvements:
                    <ul>
                      <li>
                        Improved speed and accuracy of speaker diarization.
                      </li>
                      <li>
                        Introduced word-to-number conversion in the formatted transcript.
                      </li>
                    </ul>
                  </p>
                </li>
                <li>
                  <h4>2018 Jan 31</h4>
                  <p>
                    Upgraded functionality that might break backwards-compatibility:
                    <ul>
                      <li>
                        To provide more human-readable output, <code class="prettyprint">"transcript"</code> now has capitalization, punctuation, and disfluency filtering.
                      </li>
                      <li>
                        The unformatted output is now returned in <code class="prettyprint">"transcript_raw"</code>
                      </li>
                    </ul>
                  </p>
                </li>
                <li>
                  <h4>2017 Aug 24</h4>
                  <p>
                    Convenience features:
                    <ul>
                      <li>
                        Aliased <code class="prettyprint">word_alternatives</code> to set <code class="prettyprint">word_alternatives_threshold=0.0</code>.
                      </li>
                      <li>
                        Aliased <code class="prettyprint">format="watson"</code> to set <code class="prettyprint">format="ibm"</code>.
                      </li>
                      <li>
                        New <code class="prettyprint">format="sherlock"</code> to facilitate Twilio Add-on integration
                      </li>
                    </ul>
                  </p>
                </li>
                <li>
                  <h4>2017 Aug 19</h4>
                  <p>
                    More output formats, to increase compatibility with IBM Watson:
                    <ul>
                      <li>
                        Enabled <code class="prettyprint">word_confidence</code> and <code class="prettyprint">word_alternatives_threshold</code>.
                      </li>
                      <li>
                        Enabled <code class="prettyprint">speaker_labels</code> and <code class="prettyprint">timestamps</code>.
                      </li>
                      <li>
                        Unlike IBM Watson, these parameters can be specified on either the initial POST or overriden on a later GET.
                      </li>
                      <li>
                        Coming soon: <code class="prettyprint">keywords</code>, <code class="prettyprint">max_alternatives</code>, <code class="prettyprint">interim_results</code>, <code class="prettyprint">profanity_filter</code>, and <code class="prettyprint">smart formatting</code>.
                      </li>
                    </ul>
                  </p>
                  <p>
                    Also, some custom output formats:
                    <ul>
                      <li>
                        New <code class="prettyprint">speaker_label_transcript</code> lists speaker labels for each transcript segment (more convenient than listing for each word interval).
                      </li>
                      <li>
                        New <code class="prettyprint">timestamp_transcript</code> replaces <code class="prettyprint">timestamps_transcript</code> (currently retained for compatibility, but soon to be deprecated).
                      </li>
                                            <li>
                        New <code class="prettyprint">format="remeeting"</code> parameter shows an alternative JSON view of results -- incompatible with IBM Watson, but better in several ways.
                      </li>

                    </ul>
                  </p>
                  <p>
                    Other convenience features:
                    <ul>
                      <li>
                        List the 10 most recent jobs, instead of all.
                        Also added new parameters that enable pagination and filtering: <code class="prettyprint">limit</code>, <code class="prettyprint">before</code>, and <code class="prettyprint">status</code>.
                      </li>
                      <li>
                        Jobs that are POSTed with a <code class="prettyprint">media_url</code> parameter will now display that field in GET responses.
                      </li>
                      <li>
                        The <code class="prettyprint">log_callback</code> endpoint and <code class="prettyprint">email_callback</code> parameter will now report URL query strings received on callback requests.
                      </li>
                      <li>
                        Docs have Intercom chat and phone number for technical support.
                      </li>
                    </ul>
                  </p>
                </li>
                <li>
                  <h4>2017 Apr 16</h4>
                  Initial release, primarily to enable integration in Twilio Add-on Marketplace.
                </li>
              </ul>
            </p>
          </Section.Left>
          <Section.Right empty />
        </Section>
      </div>
    </article>
  </div>
</div>
    );
  }
}

export default Docs;
